import React from 'react';
import { UseFormRegister, FieldErrors } from 'react-hook-form';
import { Employee } from '../../../types';
import Input from '../../Form/Input';
import Select from '../../Form/Select';

interface EmployeePersonalInfoProps {
  register: UseFormRegister<Employee>;
  errors: FieldErrors<Employee>;
  nationalIdFileName: string;
  setNationalIdFileName: (value: string) => void;
  documentType: string;
  setDocumentType: (value: string) => void;
  nationalityOptions: { value: string; label: string }[];
  documentTypeOptions: { value: string; label: string }[];
}

const EmployeePersonalInfo: React.FC<EmployeePersonalInfoProps> = ({
  register,
  errors,
  nationalIdFileName,
  setNationalIdFileName,
  documentType,
  setDocumentType,
  nationalityOptions,
  documentTypeOptions
}) => {
  return (
    <>
      <div className="col-span-full">
        <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2 mt-6">
          Personal Information
        </h3>
      </div>
      
      <Input
        label="National ID (الرقم القومي)"
        {...register('nationalId')}
        error={errors.nationalId?.message}
        placeholder="123456789"
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Document Type (نوع الوثيقة)
        </label>
        <select
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">Select Document Type</option>
          {documentTypeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      <div className="col-span-full">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Document File (صورة الوثيقة)
        </label>
        <div className="flex items-center justify-center w-full">
          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors">
            <div className="flex flex-col items-center justify-center pt-5 pb-6">
              {nationalIdFileName ? (
                <>
                  <svg className="w-8 h-8 mb-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className="mb-1 text-sm text-gray-700 font-medium">{nationalIdFileName}</p>
                  <p className="text-xs text-gray-500">Click to change file</p>
                </>
              ) : (
                <>
                  <svg className="w-8 h-8 mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="mb-1 text-sm text-gray-500">
                    <span className="font-semibold">Click to upload</span> document image
                  </p>
                  <p className="text-xs text-gray-500">PDF, PNG, JPG or JPEG (Max 10MB)</p>
                </>
              )}
            </div>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                setNationalIdFileName(file ? file.name : '');
              }}
            />
          </label>
        </div>
      </div>
      
      <Input
        label="ID Card Expiry Date (تاريخ انتهاء البطاقة)"
        type="date"
        {...register('cardExpiryDate')}
        error={errors.cardExpiryDate?.message}
      />
      
      <Input
        label="Birth Date (تاريخ الميلاد)"
        type="date"
        {...register('birthDate')}
        error={errors.birthDate?.message}
      />
      
      <Select
        label="Nationality (الجنسية)"
        {...register('nationality')}
        options={nationalityOptions}
        error={errors.nationality?.message}
      />
      
      <Input
        label="Address (العنوان)"
        {...register('address')}
        error={errors.address?.message}
        placeholder="Full address"
        className="md:col-span-2"
      />
    </>
  );
};

export default EmployeePersonalInfo;
